import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import {
	getClassroomInvoices,
	getCourseInvoices,
} from '@Features/classrooms/classroomSlice';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import { TableHeader } from '@Features/generics/generics.type';
import {
	Box,
	Collapse,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from '@mui/material';
import { deepPurple, green, orange, red, yellow } from '@mui/material/colors';
import {
	AnalyticsCardsContainer,
	EnrollmentsCard,
	EnrollmentTypeCard,
	TypeInfo,
	TypeName,
	TypeValue,
	enrollmentIcons,
} from '@Components/UI/Analytics/Analytics.styled';
import { AnalyticsTotal } from '@Components/UI/Analytics/Assets/Icons/Icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

type InvoicesScope = 'classroom' | 'course';
type InvoiceMethod = 'wallet' | 'scratchcard' | 'free' | 'admin' | 'instructor';

const TABLE_HEADS: TableHeader[] = [
	{
		displayName: 'Lecture',
		fieldName: 'course_name',
		canSort: false,
		width: '18%',
	},
	{
		displayName: 'Price Groups',
		fieldName: 'price_groups',
		canSort: false,
		width: '10%',
	},
	{
		displayName: 'Wallet',
		fieldName: 'wallet',
		canSort: false,
		width: '12%',
	},
	{
		displayName: 'Scratchcard',
		fieldName: 'scratchcard',
		canSort: false,
		width: '12%',
	},
	{
		displayName: 'Admin',
		fieldName: 'admin',
		canSort: false,
		width: '12%',
	},
	{
		displayName: 'Instructor',
		fieldName: 'instructor',
		canSort: false,
		width: '12%',
	},
	{
		displayName: 'Free',
		fieldName: 'free',
		canSort: false,
		width: '8%',
	},
	{
		displayName: 'Enrollments',
		fieldName: 'enrollments',
		canSort: false,
		width: '8%',
	},
	{
		displayName: 'Total Sales',
		fieldName: 'grand_total',
		canSort: false,
		width: '8%',
	},
];

const DETAIL_HEADS = [
	'Lec. Price - Inv. Price',
	'Note',
	'Wallet',
	'Scratchcard',
	'Admin',
	'Instructor',
	'Free',
	'Enrollments',
	'Row Total',
];
const TABLE_COLUMN_WIDTHS = TABLE_HEADS.map((head) => head.width);
const METHOD_KEYS: InvoiceMethod[] = [
	'wallet',
	'scratchcard',
	'free',
	'admin',
	'instructor',
];
const METHOD_LABELS: Record<InvoiceMethod, string> = {
	wallet: 'Wallet',
	scratchcard: 'Scratchcard',
	free: 'Free',
	admin: 'Admin',
	instructor: 'Instructor',
};

// Exported: the Analytics hub and the standalone/bundle Invoices subtabs
// reuse the same money formatting so every financial surface reads alike.
export const money = (value?: number) => Number(value ?? 0).toLocaleString();

export const egp = (value?: number) => `${money(value)} EGP`;

export const average = (count?: number, total?: number) =>
	Number(count ?? 0) ? Number(total ?? 0) / Number(count ?? 0) : 0;

const rowTotal = (row: any) =>
	METHOD_KEYS.reduce(
		(total, method) => total + Number(row[method].total ?? 0),
		0
	);

const rowEnrollments = (row: any) =>
	METHOD_KEYS.reduce(
		(total, method) => total + Number(row[method].count ?? 0),
		0
	);

export const methodFormula = (row: any, method: string) => {
	const count = Number(row[method].count ?? 0);
	const total = Number(row[method].total ?? 0);

	if (!count) return '0';
	if (method === 'free') return `${count} free`;

	return `${count} x ${money(row.effective_price_snapshot)} = ${money(total)}`;
};

export const MethodSummaryCell = ({
	summary,
	isFree = false,
}: {
	summary: { count?: number; total?: number };
	isFree?: boolean;
}) => {
	const count = Number(summary.count ?? 0);
	const total = Number(summary.total ?? 0);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '0.25rem',
				lineHeight: 1.2,
			}}
		>
			<CompactValue>{egp(total)}</CompactValue>
			<Box
				component="span"
				sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
			>
				{isFree || !count
					? `${count} enrolls`
					: `avg ${egp(average(count, total))} - ${count} enrolls`}
			</Box>
		</Box>
	);
};

export const CompactValue = ({ children }: { children: React.ReactNode }) => (
	<Box
		component="span"
		sx={{
			color: 'text.primary',
			fontSize: '0.875rem',
			fontWeight: 600,
			lineHeight: 1.2,
			whiteSpace: 'nowrap',
		}}
	>
		{children}
	</Box>
);

const EnrollmentInvoices: React.FC<{ scope: InvoicesScope }> = ({ scope }) => {
	const dispatch = useAppThunkDispatch();
	const { id } = useParams<{ id: string }>();
	const { currentClassroom } = useTypedSelector((state) => state.classroom);
	const invoicesState = useTypedSelector((state) =>
		scope === 'classroom'
			? state.classroom.analytics.currentClassroom.invoices
			: state.classroom.analytics.currentCourse.invoices
	);
	const [expanded, setExpanded] = useState<Record<number, boolean>>({});
	const summaryCards = useMemo(() => {
		const totals = METHOD_KEYS.reduce(
			(acc, method) => ({ ...acc, [method]: 0 }),
			{} as Record<InvoiceMethod, number>
		);
		const counts = METHOD_KEYS.reduce(
			(acc, method) => ({ ...acc, [method]: 0 }),
			{} as Record<InvoiceMethod, number>
		);
		let totalEnrollments = 0;
		let grandTotal = 0;

		invoicesState.data.forEach((course) => {
			grandTotal += course.summary.grand_total;
			METHOD_KEYS.forEach((method) => {
				const count = course.summary[method].count;
				totals[method] += course.summary[method].total;
				counts[method] += count;
				totalEnrollments += count;
			});
		});

		return { counts, grandTotal, totals, totalEnrollments };
	}, [invoicesState.data]);

	const pickTypeColor = useCallback((type: InvoiceMethod) => {
		if (type === 'admin') return red[500];
		if (type === 'wallet') return deepPurple[500];
		if (type === 'scratchcard') return orange[500];
		if (type === 'free') return yellow[600];
		return green['A700'];
	}, []);

	const toggleExpanded = useCallback((courseId: number) => {
		setExpanded((prev) => ({
			...prev,
			[courseId]: !prev[courseId],
		}));
	}, []);

	const fetchInvoices = useCallback(
		({
			activePage,
			endDate,
			query,
			startDate,
			perPage,
		}: onPageChangeRequestMeta) => {
			if (scope === 'classroom') {
				if (!currentClassroom?.id) return;
				dispatch(
					getClassroomInvoices({
						page: activePage,
						perPage: perPage ?? 10,
						classroom_id: currentClassroom.id,
						filters: [],
						sortBy: undefined,
						query,
						from: startDate,
						to: endDate,
					})
				);
				return;
			}

			dispatch(
				getCourseInvoices({
					page: activePage,
					perPage: perPage ?? 10,
					course_id: Number(id),
					query,
					from: startDate,
					to: endDate,
				})
			);
		},
		[currentClassroom?.id, dispatch, id, scope]
	);

	useEffect(() => {
		const startDate = new Date();
		startDate.setDate(startDate.getDate() - 14);
		fetchInvoices({
			activePage: 1,
			perPage: 10,
			filters: [],
			sortBy: undefined,
			query: '',
			startDate,
			endDate: new Date(),
		});
	}, [fetchInvoices]);

	return (
		<>
			<Box
				sx={{
					'& > div': {
						flexWrap: 'wrap',
						gap: '1rem',
						marginBottom: '2rem',
					},
					'& > div > *': {
						margin: '0 !important',
					},
				}}
			>
				<AnalyticsCardsContainer>
					<EnrollmentsCard elevation={10}>
						<AnalyticsTotal />

						<TypeInfo>
							<span>{money(summaryCards.grandTotal)}</span>
							<label>Total Sales</label>
							<Box
								component="small"
								sx={{ color: 'inherit', fontSize: '0.85rem', fontWeight: 500 }}
							>
								({summaryCards.totalEnrollments} enrollments)
							</Box>
						</TypeInfo>
					</EnrollmentsCard>
					{METHOD_KEYS.map((method) => (
						<EnrollmentTypeCard key={method} color={pickTypeColor(method)}>
							<TypeInfo>
								{enrollmentIcons[method]}
								<TypeValue>{money(summaryCards.totals[method])}</TypeValue>
							</TypeInfo>
							<TypeName>
								<Box>{METHOD_LABELS[method]}</Box>
								<Box
									component="small"
									sx={{
										color: 'inherit',
										fontSize: '0.75rem',
										fontWeight: 500,
									}}
								>
									({summaryCards.counts[method]} enrollments)
								</Box>
							</TypeName>
						</EnrollmentTypeCard>
					))}
				</AnalyticsCardsContainer>
			</Box>
			<Box
				sx={{
					'& .MuiTableCell-head': {
						whiteSpace: 'normal !important',
						lineHeight: 1.2,
						padding: '0 0.5rem',
						fontSize: '0.875rem !important',
					},
					'& .MuiTableSortLabel-root': {
						whiteSpace: 'normal',
						textAlign: 'center',
					},
					'& .MuiTableCell-body': {
						verticalAlign: 'middle',
					},
				}}
			>
				<MainTable
					tableHeads={TABLE_HEADS}
					tableFilters={[]}
					totalPages={invoicesState.meta?.last_page ?? 1}
					total={invoicesState.meta?.total ?? invoicesState.data.length}
					RowsperPage={invoicesState.meta?.per_page ?? 10}
					pageNum={invoicesState.meta?.current_page ?? 1}
					onPageChangeRequest={fetchInvoices}
					renderItems={() => {
						if (!invoicesState.data.length) {
							return (
								<MainTableRow>
									<TableCell colSpan={TABLE_HEADS.length}>
										<NoResults />
									</TableCell>
								</MainTableRow>
							);
						}

						return invoicesState.data.map((course) => (
							<React.Fragment key={course.course_id}>
								<MainTableRow
									hover
									role="button"
									tabIndex={0}
									aria-expanded={expanded[course.course_id]}
									onClick={() => toggleExpanded(course.course_id)}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											toggleExpanded(course.course_id);
										}
									}}
									sx={{
										'& > td': {
											backgroundColor: expanded[course.course_id]
												? 'rgba(108, 99, 255, 0.04)'
												: 'inherit',
										},
									}}
								>
									<TableCell align="center">{course.course_name}</TableCell>
									<TableCell align="center">
										<CompactValue>{course.rows.length}</CompactValue>
									</TableCell>
									<TableCell align="center">
										<MethodSummaryCell summary={course.summary.wallet} />
									</TableCell>
									<TableCell align="center">
										<MethodSummaryCell summary={course.summary.scratchcard} />
									</TableCell>
									<TableCell align="center">
										<MethodSummaryCell summary={course.summary.admin} />
									</TableCell>
									<TableCell align="center">
										<MethodSummaryCell summary={course.summary.instructor} />
									</TableCell>
									<TableCell align="center">
										<MethodSummaryCell summary={course.summary.free} isFree />
									</TableCell>
									<TableCell align="center">
										<CompactValue>
											{METHOD_KEYS.reduce(
												(total, method) => total + course.summary[method].count,
												0
											)}
										</CompactValue>
									</TableCell>
									<TableCell align="center">
										<CompactValue>
											{egp(course.summary.grand_total)}
										</CompactValue>
									</TableCell>
								</MainTableRow>
								<TableRow>
									<TableCell colSpan={TABLE_HEADS.length} sx={{ p: 0 }}>
										<Collapse
											in={expanded[course.course_id]}
											timeout="auto"
											unmountOnExit
										>
											<Box sx={{ py: 1, overflowX: 'auto' }}>
												<Table
													size="small"
													sx={{ minWidth: 900, tableLayout: 'fixed' }}
												>
													<colgroup>
														{TABLE_COLUMN_WIDTHS.map((width, index) => (
															<col
																key={`${width}-${index}`}
																style={{ width }}
															/>
														))}
													</colgroup>
													<TableHead>
														<TableRow>
															{DETAIL_HEADS.map((head) => (
																<TableCell align="center" key={head}>
																	{head}
																</TableCell>
															))}
														</TableRow>
													</TableHead>
													<TableBody>
														{course.rows.map((row, index) => (
															<TableRow
																key={`${course.course_id}-${row.lecture_price_snapshot}-${row.effective_price_snapshot}-${row.note}-${index}`}
															>
																<TableCell align="center">
																	{money(row.lecture_price_snapshot)} -{' '}
																	{money(row.effective_price_snapshot)}
																</TableCell>
																<TableCell align="center">{row.note}</TableCell>
																<TableCell align="center">
																	{methodFormula(row, 'wallet')}
																</TableCell>
																<TableCell align="center">
																	{methodFormula(row, 'scratchcard')}
																</TableCell>
																<TableCell align="center">
																	{methodFormula(row, 'admin')}
																</TableCell>
																<TableCell align="center">
																	{methodFormula(row, 'instructor')}
																</TableCell>
																<TableCell align="center">
																	{methodFormula(row, 'free')}
																</TableCell>
																<TableCell align="center">
																	{rowEnrollments(row)}
																</TableCell>
																<TableCell align="center">
																	{egp(rowTotal(row))}
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</Box>
										</Collapse>
									</TableCell>
								</TableRow>
							</React.Fragment>
						));
					}}
				/>
			</Box>
		</>
	);
};

export const ClassroomInvoices = () => <EnrollmentInvoices scope="classroom" />;
export const CourseInvoices = () => <EnrollmentInvoices scope="course" />;
